import { Float32BufferAttribute, Mesh, type BufferGeometry } from 'three';
import type { AABB, PolygonWithHoles, Vec2 } from '../../geo/types';
import type { SiteModel } from '../../site/types';
import type { PaletteModel } from '../../data/types';
import type { AtmosphereParams, MaterialFactory } from '../../render/types';
import type { VertexStyle } from '../mesh/meshBuilder';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { SURFACE, EDGE_ID } from '../mesh/attributes';
import { WORLD } from '../../core/config';
import type { DensifyConfig, RefinementZone } from './densify';
import { groundStyle, type FieldRegionClasses } from './groundStyle';
import {
  distanceBeyond,
  perimeterOf,
  radialArcOf,
  rampDistance,
  seamRuns,
  type SeamRun,
} from './farRingSeam';
import { farRingSectors } from './farRingSectors';
import {
  createSeamField,
  ditchContrast,
  windowHalfWidth,
  type SeamColourRun,
  type SeamField,
} from './farRingField';
import { withDitchContrast } from './ditchContrast';
import { createRunEnds, type RunEnds } from './runEnds';

// Ramp vertices this far apart keep the linear colour within 1% of the smoothstep blend.
const RAMP_EDGE_SHARE = 1 / 8;

// Where the colour turns across the ring it does so over a window twice as wide as the distance out, or
// from the run end, so edges half that give each turn four vertices, down to the finest edge by the seam.
const LATERAL_EDGE_SHARE = 1 / 2;
const FINEST_EDGE_M = 100;

// A side from a seam vertex rising no more steeply than this keeps close to the seam, where the colour
// is the seam's own; that is all a triangle standing on a long seam edge needs.
const ALONG_SEAM_SLOPE = 1 / 4;

// The final pass inks no id below 2, so far-ring land written with the water's id draws no line along
// the seam, nor along the radial edges between sectors that continue different pieces.
const UNINKED_EDGE_ID = EDGE_ID.water;

export interface FarRingSeamStyle {
  readonly fields: FieldRegionClasses;
  readonly atmosphere: AtmosphereParams;
}

interface Part {
  readonly polygon: PolygonWithHoles;
  readonly style: VertexStyle;
  readonly own: SeamColourRun | null;
}

interface Range {
  readonly start: number;
  readonly end: number;
  readonly own: SeamColourRun | null;
}

function plainStyle(palette: PaletteModel): VertexStyle {
  return {
    color: palette.nearFields.median.linear,
    surface: SURFACE.ground,
    edgeId: UNINKED_EDGE_ID,
    seed: 0.5,
    strokeAngle: 0,
    extent: [0, 0],
  };
}

function colourRunOf(run: SeamRun, palette: PaletteModel, seam: FarRingSeamStyle): SeamColourRun {
  const albedo = groundStyle(run.piece, palette, seam.fields).color;
  const distance = rampDistance(albedo, palette.nearFields.median.linear, seam.atmosphere);
  return { from: run.from, to: run.to, albedo, distance };
}

// Each sector carries on the surface and ditches of the piece it continues; its colour and
// ditch contrast come from the seam field, which blurs the edges between sectors and turns to
// nearFields, the far ring's albedo under the painter-view fog, where the fog hides the difference.
function partsOf(
  site: SiteModel,
  palette: PaletteModel,
  seam: FarRingSeamStyle,
  runs: ReadonlyMap<SeamRun, SeamColourRun>
): Part[] {
  const { sectors, remainder } = farRingSectors(site, [...runs.keys()], WORLD.farRingRadius);
  const continued = sectors.flatMap(({ run, polygons }) => {
    const style = { ...groundStyle(run.piece, palette, seam.fields), edgeId: UNINKED_EDGE_ID };
    return polygons.map((polygon) => ({ polygon, style, own: runs.get(run) ?? null }));
  });
  const rest = remainder.map((polygon) => ({ polygon, style: plainStyle(palette), own: null }));
  return [...continued, ...rest];
}

// Edges along the seam keep the vertices the ground inside the bounds shares. Off it every edge within
// the ramp follows the smoothstep outward, and where a window reaches a run end the edges shrink towards
// that end, as the blend between neighbouring sectors narrows towards the seam. A triangle standing on a
// seam edge from one run end to the next cannot bring the sides from both ends down to the finest edge,
// so a side from a seam vertex that runs more along the bounds than out from them keeps to the ramp's
// length; the side that runs out from the run end, along the sector edge, still shrinks towards it.
function seamZone(bounds: AABB, ramp: number, ends: RunEnds): RefinementZone {
  const radial = ramp * RAMP_EDGE_SHARE;
  const perimeter = perimeterOf(bounds);
  const alongSeam = (s: Vec2, q: Vec2, beyondQ: number) => {
    const apart = Math.abs(radialArcOf(q, bounds) - radialArcOf(s, bounds));
    return Math.min(apart, perimeter - apart) * ALONG_SEAM_SLOPE > beyondQ;
  };
  return {
    longestEdge(a, b) {
      const [atA, atB] = [distanceBeyond(a, bounds), distanceBeyond(b, bounds)];
      const atMiddle = distanceBeyond([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], bounds);
      if (Math.max(atA, atB, atMiddle) === 0) return Infinity;
      const nearest = Math.min(atA, atB, atMiddle);
      if (nearest >= ramp) return Infinity;
      if ((atA === 0 && alongSeam(a, b, atB)) || (atB === 0 && alongSeam(b, a, atA))) return radial;
      // The distance beyond the bounds is convex along a segment, so its ends lie farthest out.
      const gap = ends.gap(a, b);
      if (gap > windowHalfWidth(Math.max(atA, atB))) return radial;
      return Math.min(radial, Math.max(FINEST_EDGE_M, Math.max(nearest, gap) * LATERAL_EDGE_SHARE));
    },
  };
}

function sampledField(
  geometry: BufferGeometry,
  ranges: readonly Range[],
  field: SeamField
): BufferGeometry {
  const position = geometry.getAttribute('position');
  const colours = new Float32Array(position.count * 3);
  const contrasts = new Float32Array(position.count);
  for (const { start, end, own } of ranges) {
    for (let v = start; v < end; v++) {
      const { colour, share } = field.sample([position.getX(v), -position.getZ(v)], own);
      colours.set(colour, v * 3);
      contrasts[v] = ditchContrast(share);
    }
  }
  geometry.setAttribute('color', new Float32BufferAttribute(colours, 3));
  return withDitchContrast(geometry, (v) => contrasts[v]!);
}

export function buildFarRingLandMesh(
  site: SiteModel,
  palette: PaletteModel,
  materials: MaterialFactory,
  seam: FarRingSeamStyle,
  densifyConfig?: DensifyConfig
): Mesh {
  const runs = new Map(
    seamRuns(site.ground, site.bounds).map((run) => [run, colourRunOf(run, palette, seam)])
  );
  const colourRuns = [...runs.values()];
  const field = createSeamField(colourRuns, site.bounds, palette.nearFields.median.linear);
  const ramp = Math.max(0, ...colourRuns.map((run) => run.distance));
  const ends = createRunEnds(colourRuns, site.bounds);
  const refinement = densifyConfig && {
    ...densifyConfig,
    zone: seamZone(site.bounds, ramp, ends),
  };

  const builder = createMeshBuilder();
  const ranges = partsOf(site, palette, seam, runs).map(({ polygon, style, own }): Range => {
    const start = builder.vertexCount;
    builder.groundPolygon(style, polygon, WORLD.groundY, refinement);
    return { start, end: builder.vertexCount, own };
  });

  const mesh = new Mesh(sampledField(builder.build(), ranges, field), materials.painted());
  mesh.matrixAutoUpdate = false;
  mesh.frustumCulled = true;
  return mesh;
}
